import {BuildingOwnership} from './BuildingOwnership';import {SetupAccess} from './SetupAccess';import {BuildingKeysAccess} from './BuildingKeysAccess';
import {featureOptions,featureAssetDefinitions} from './building-feature-definitions';
import {BuildingSetupSummary} from './BuildingSetup';
import {BuildingOverviewOnboarding} from './BuildingOnboardingMarkers';
import {saveRequest} from '../ui/save-request';
import type {ManagementStatusModel} from './BuildingManagementStatus';
import {BuildingManagementStatus} from './BuildingManagementStatus';
import {ComplianceTruthBanner} from '../ui/ComplianceTruthBanner';
import {buildingSetupErrors,type BuildingFieldErrors} from '@myfm/domain';
import {showFormErrors} from '../ui/form-errors';
import { managementStatus } from '../ui/management-status';
import {BuildingOperationalAttention,useBuildingOperationalAttention} from './BuildingOperationalAttention';
import { useCapability, useStaffSession } from '../ui/StaffSession';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from '../ui/ScopedLink';
import { withBuildingScope } from '../ui/building-scope';
import { useNavigate, useParams, useLocation,type NavigateOptions } from 'react-router';
import {safeWorkflowReturn,withWorkflowReturn} from '../ui/WorkflowReturn';
import { api } from "../api";
import { isApiError } from "../api-error";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateOnly, formatDateTime } from "../dates";
import type { ComplianceTruthProjection } from "../presentation/compliance-truth";
import { Empty, ErrorState, Loading, State, reviewedApplicationError } from "../ui/State";
import { RelatedDocuments } from "./RelatedDocuments";
import { RelatedWork } from "./RelatedWork";
import { RelatedCompliance } from "./RelatedCompliance";
import { TimelinePanel } from "./TimelinePanel";
import { KeyDocuments } from "./KeyDocuments";
import { PendingDocumentsCard } from "../components/PendingDocumentsCard";
import {BuildingLocationMap} from "../ui/BuildingLocationMap";
import {BuildingLocationEditor} from "../ui/BuildingLocationEditor";
import {recordedGeoPoint,type GeoPoint} from "../ui/geo-map";
import {StatusBadge} from '../ui/StatusBadge';
import {LazyDetails} from '../ui/LazyDetails';
import {Icon} from '../ui/Icon';
import {BuildingPhoto} from '../ui/BuildingPhoto';
import {PageHeader} from '../ui/PageHeader';
import './building-overview.css';
import './building-setup-feedback.css';
import './operational-dashboard.css';
type Building = {
  id: string;
  code: string;
  name: string;
  status: string;
  management_status: string;
  residential_unit_count: number;
  commercial_unit_count: number;
  communal_facility_count: number;
  asset_count: number;
};
type Feature = {
  code: string;
  label: string;
  category: string;
  status: "Present" | "Not present" | "Unknown" | "Requires survey";
  notes?: string;
};
type OnboardingAsset = {
  sourceHash?: string;
  id: string;
  code: string;
  label: string;
  category: string;
  type: string;
  criticality: string;
  presence: "Present" | "To confirm" | "Not present";
  quantity?: number;
  quantity_status: "Known" | "To confirm" | "Not applicable";
  location?: string;
  notes?: string;
  status: string;
};
type OnboardingDocument = {
  batch_id: string;
  batch_number: string;
  source_name: string;
  batch_status: string;
  item_id: string;
  original_file_name: string;
  content_status: string;
  scan_verdict: string;
  processing_status: string;
  failure_reason?: string;
  content_uploaded_at?: string;
};
type Setup = Building & {
  canConfirmLocation:boolean;
  latitude?:number;
  longitude?:number;
  line_1: string;
  line_2?: string;
  town_city: string;
  county?: string;
  postcode: string;
  country_code: string;
  expected_residential_units: number;
  expected_commercial_units?: number;
  commercial_unit_status: "Yes" | "No" | "To confirm";
  expected_communal_facilities: number;
  communal_areas?:string[]|null;
  mobilisationProgrammeId?:string|null;
  communal_area_other?:string|null;
  configuration?:{source?:string};
  management_start_date?: string;
  management_end_date?: string;
  onboarding_narrative?: string;
  building_use: string;
  height_metres?: number | string;
  storeys?: number;
  hrb_status: string;
  high_rise_status: "Yes" | "No" | "To confirm";
  recorded_residential_units: number;
  recorded_commercial_units: number;
  recorded_communal_facilities: number;
  unaddressed_unit_count?: number;
  keyContacts: Array<{
    id: string;
    role_name: string;
    contact_name: string;
    is_primary_contact: boolean;
    status: string;
  }>;
  features: Feature[];
  initialAssets: OnboardingAsset[];
  onboardingDocuments: OnboardingDocument[];
  readiness: Record<
    "structure" | "contacts" | "regulatory" | "features",
    "Ready" | "Attention" | "Blocked"
  >;
};
type Area = {
  id: string;
  premises_id?: string;
  node_type: string;
  display_name: string;
  status: string;
  premises_type?: string;
  asset_count: number;
};
type Asset = {
  id: string;
  asset_code: string;
  display_name: string;
  asset_category: string;
  asset_type: string;
  status: string;
  criticality: string;
  property_name: string;
  equipment_quantity?: number;
  quantity_status?: string;
  onboarding_presence?: string;
};
function commercialDeclarationLabel(
  status: Setup["commercial_unit_status"],
  count?: number,
) {
  if (status === "No") return "0 commercial units";
  if (status === "To confirm") return "Commercial units: To confirm";
  return count == null ? "Commercial units: To confirm" : `${count} commercial units`;
}
function CommercialDeclarationFields({ setup }: { setup: Setup }) {
 const [count,setCount]=useState(setup.expected_commercial_units==null?setup.commercial_unit_status==='No'?'0':'':String(setup.expected_commercial_units));
 useEffect(()=>setCount(setup.expected_commercial_units==null?setup.commercial_unit_status==='No'?'0':'':String(setup.expected_commercial_units)),[setup.expected_commercial_units,setup.commercial_unit_status]);
 return <label>Number of commercial premises<input name="expectedCommercialUnits" type="number" min="0" step="1" required value={count} onChange={event=>setCount(event.target.value)}/><input type="hidden" name="commercialUnitStatus" value={count===''?'To confirm':Number(count)>0?'Yes':'No'}/><small>Enter 0 when there are none.</small></label>;
}
function PremisesList({
  buildingId,
  title,
  items,
  empty,
}: {
  buildingId: string;
  title: string;
  items: Area[];
  empty: string;
}) {
  return (
    <section className="panel building-section">
      <div className="section-heading">
        <h2>{title}</h2>
        <span>{items.length}</span>
      </div>
      {!items.length ? (
        <Empty>{empty}</Empty>
      ) : (
        <div className="premises-grid">
          {items.map((item) => (
            <Link
              key={item.id}
              to={
                item.premises_id
                  ? `/buildings/${buildingId}/properties/${item.premises_id}`
                  : `/buildings/areas/${item.id}`
              }
            >
              <strong>{item.display_name}</strong>
              <small>{item.status}</small>
              <span>View property <Icon name="arrow-right"/></span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function unitSequence(item: Area) {
  const match = item.display_name.match(/(\d+)\s*$/);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}
function UnitSelector({
  buildingId,
  residential,
  commercial,
  onSelect,
}: {
  buildingId: string;
  residential: Area[];
  commercial: Area[];
  onSelect: (path: string) => void;
}) {
  const ordered = [...residential, ...commercial];
  return (
    <section className="panel building-section residential-selector">
      <div>
        <span className="eyebrow">Premises</span>
        <h2>{residential.length} flat{residential.length === 1 ? "" : "s"}{commercial.length ? ` · ${commercial.length} commercial unit${commercial.length === 1 ? "" : "s"}` : ""}</h2>
        <p>
          <Link to={withBuildingScope(`/buildings/${buildingId}/mobilisation/premises`,buildingId)}>Open premises register</Link>
        </p>
      </div>
      <label>
        View premises
        <select
          defaultValue=""
          onChange={(event) => {
            if (event.target.value) onSelect(event.target.value);
          }}
        >
          <option value="" disabled>
            Select a unit
          </option>
          {ordered.map((item) => (
            <option
              key={item.id}
              value={
                item.premises_id
                  ? `/buildings/${buildingId}/properties/${item.premises_id}`
                  : `/buildings/areas/${item.id}`
              }
            >
              {item.display_name}
            </option>
          ))}
        </select>
      </label>
    </section>
  );
}
function buildingType(setup: Setup) {
  if (setup.hrb_status === "Confirmed HRB") return "Regulatory HRB: confirmed";
  if (Number(setup.height_metres ?? 0) >= 18 || (setup.storeys ?? 0) >= 7)
    return "High-rise building: Yes";
  if (setup.high_rise_status === "No") return "High-rise building: No";
  return "High-rise building: To confirm";
}
function BuildingPropertyCounts({setup}:{setup:Setup}) {
  const register=withBuildingScope(`/buildings/${setup.id}/mobilisation/premises`,setup.id);
  const expectedCommercial=setup.commercial_unit_status==='No'?0:setup.commercial_unit_status==='Yes'?setup.expected_commercial_units:undefined;
  const recordedKnown=[setup.recorded_residential_units,setup.recorded_commercial_units].every(value=>Number.isSafeInteger(value)&&value>=0);
  const discrepancy=setup.recorded_residential_units!==setup.expected_residential_units||expectedCommercial==null||setup.recorded_commercial_units!==expectedCommercial;
  return <div aria-label="Property counts">
    <div className="building-known-facts"><Link to={register}>{setup.expected_residential_units==null?'Residential count: To confirm':`${setup.expected_residential_units} flats`}</Link><Link to={register}>{commercialDeclarationLabel(setup.commercial_unit_status,setup.expected_commercial_units)}</Link></div>
    <p>Declared property counts. Open a count to review the premises register.</p>
    {!recordedKnown?<p>Recorded property counts are unavailable.</p>:discrepancy&&<p>Register records {setup.recorded_residential_units} flats and {setup.recorded_commercial_units} commercial units. Review the declarations and register; existing properties remain available.</p>}
  </div>;
}

function BuildingAddressLocation({building,setup,canManage,complianceTruth,focused=false}:{building:Building;setup:Setup;canManage:boolean;complianceTruth?:ComplianceTruthProjection|undefined;focused?:boolean}) {
  const displayName=building.name.replace(' - Controlled Demo','');
  const fullAddress=[setup.line_1,setup.line_2,setup.town_city,setup.county,setup.postcode,setup.country_code].filter(Boolean).join(', ');
  const [addressCopied, setAddressCopied] = useState(false);
  const [copyFailed,setCopyFailed]=useState(false);
  const copyTimer=useRef<number | undefined>(undefined);
  const activeSummary=useRef(true);
  useEffect(()=>{activeSummary.current=true;return()=>{activeSummary.current=false;window.clearTimeout(copyTimer.current);};},[]);
  const [confirmedLocation,setConfirmedLocation]=useState<GeoPoint|undefined>(()=>recordedGeoPoint(setup.latitude,setup.longitude));
  const [mapLocation,setMapLocation]=useState<GeoPoint|undefined>(confirmedLocation);
  const [locationEditing,setLocationEditing]=useState(false),[locationSaving,setLocationSaving]=useState(false),[locationUncertain,setLocationUncertain]=useState(false),[locationError,setLocationError]=useState<unknown>(),[locationNotice,setLocationNotice]=useState('');
  const locationSubmitting=useRef(false);
  const [mapLocationOrigin,setMapLocationOrigin]=useState<'Postcode estimate'|'Adjusted by staff'|'Manual coordinates'>(mapLocation?'Manual coordinates':'Postcode estimate');
  const copyAddress = async () => {
    try { await navigator.clipboard.writeText(`${displayName}\n${fullAddress}`);if(!activeSummary.current)return;setCopyFailed(false);setAddressCopied(true);window.clearTimeout(copyTimer.current);copyTimer.current=window.setTimeout(()=>{if(activeSummary.current)setAddressCopied(false);},2000); }
    catch { if(activeSummary.current){setAddressCopied(false);setCopyFailed(true);} }
  };
  const confirmLocation=async(event:FormEvent<HTMLFormElement>)=>{
    event.preventDefault();if(!canManage||!setup.canConfirmLocation||!mapLocation||locationSubmitting.current||locationUncertain)return;
    const data=new FormData(event.currentTarget),point={latitude:Number(mapLocation.latitude.toFixed(6)),longitude:Number(mapLocation.longitude.toFixed(6))};
    locationSubmitting.current=true;setLocationSaving(true);setLocationError(undefined);setLocationNotice('');
    try {
      const receipt=await api<{id:string;latitude:number;longitude:number}>(withBuildingScope(`/api/property-assets/developments/${building.id}/location`,building.id),{method:'PUT',body:JSON.stringify({...point,source:'Staff map confirmation',note:String(data.get('note'))})});
      if(!activeSummary.current)return;
      if(receipt.id!==building.id||receipt.latitude!==point.latitude||receipt.longitude!==point.longitude){setLocationUncertain(true);return;}
      setConfirmedLocation(point);setMapLocation(point);setLocationNotice('Map location saved.');
    } catch(cause) {if(activeSummary.current){if(isApiError(cause)&&cause.statusCode>=400&&cause.statusCode<500)setLocationError(cause);else setLocationUncertain(true);}}
    finally {locationSubmitting.current=false;if(activeSummary.current)setLocationSaving(false);}
  };
  return (<section className={focused?"panel building-section building-location-summary building-location-focused":"building-location-summary"} aria-label={focused?"Address and location":"Building location and map"}>
        {focused&&<><h2>Address and location</h2><p>{fullAddress||'Address not recorded'}</p></>}
        {copyFailed&&<p role="status">Copy is unavailable. Select the recorded address to copy it manually.</p>}
        {focused&&!confirmedLocation?<p>No confirmed map location is recorded.</p>:<BuildingLocationMap single buildings={[{id:building.id,name:displayName,latitude:confirmedLocation?.latitude??null,longitude:confirmedLocation?.longitude??null,residential_units:setup.recorded_residential_units,commercial_units:setup.recorded_commercial_units,declared_residential_units:setup.expected_residential_units,declared_commercial_units:setup.expected_commercial_units??0,active_work:null,address:fullAddress,compliance_truth:complianceTruth}]}/>}
        <div className="location-actions"><button type="button" className="secondary" onClick={()=>void copyAddress()}>{addressCopied?"Address copied":"Copy address"}</button></div>
        {locationNotice&&<p role="status" className="success">{locationNotice}</p>}
        {Boolean(locationError)&&<ErrorState error={locationError}/>}
        {locationUncertain&&<State kind="unavailable" tone="warning" heading="Check the saved map location" headingLevel={3} message="The save could not be confirmed. Reload the recorded location before starting another edit." secondaryAction={{label:'Reload recorded location',onClick:()=>window.location.reload()}}/>}
{canManage && setup.canConfirmLocation&&<details className="confirm-location" onToggle={event=>setLocationEditing(event.currentTarget.open)}><summary>{confirmedLocation?'Adjust pin position':'Set map location'}</summary>{locationEditing&&<form onSubmit={confirmLocation}><fieldset disabled={locationSaving}><BuildingLocationEditor editablePostcode postcode={setup.postcode} value={mapLocation} onChange={(next,origin)=>{setMapLocation(next);setMapLocationOrigin(origin)}}/><label>Confirmation note<input name="note" required minLength={3} defaultValue={mapLocationOrigin==='Postcode estimate'?'Postcode estimate reviewed for operational mapping':'Map pin reviewed and refined by staff'}/></label><button disabled={!mapLocation||locationSaving||locationUncertain}>{locationSaving?'Saving…':'Save map location'}</button></fieldset></form>}</details>}
      </section>);
}

function BuildingSummary({
  building,
  setup,
  communal,
  assets,
  complianceTruth,
  onEdit,
  canManage,
  attention,
}: {
  building: Building;
  setup: Setup;
  communal: number|undefined;
  assets: number|undefined;
  attention: ReactNode;
  complianceTruth?: ComplianceTruthProjection | undefined;
  onEdit: () => void;
  canManage: boolean;
}) {
  const address = [setup.line_1, setup.line_2, setup.town_city, setup.county].filter(Boolean).join(", ");
  const displayName = building.name.replace(" - Controlled Demo", "");
  const fullAddress = [address, setup.postcode, setup.country_code].filter(Boolean).join(", ");
  const readinessItems = Object.entries(setup.readiness).filter(([, status]) => status !== 'Ready');
  const informationGaps = [
    !setup.management_start_date && 'No management start date is recorded.',
    setup.high_rise_status === 'To confirm' && 'Whether this building is 18 metres or more is still to confirm.',
  ].filter(Boolean) as string[];
  const canViewPhoto=useCapability('documents:view');
  const canViewCompliance=useCapability('compliance:view');
  return (
    <section className="operational-dashboard building-facts building-summary">
      <PageHeader title={displayName} context="Building overview" primaryAction={canManage?{kind:'button',label:'Edit building',icon:'settings',onClick:onEdit}:undefined}/>
      <div className="building-overview-facts" aria-label="Known building facts">
       <div><span>Address</span><strong>{fullAddress||'Address not recorded'}</strong></div>
       <div><span>Units</span><strong>{setup.expected_residential_units} flats · {commercialDeclarationLabel(setup.commercial_unit_status,setup.expected_commercial_units)}</strong></div>
       <BuildingOwnership building={building.id} compact/>
       <div><span>Operational management</span><BuildingManagementStatus building={building.id} compact/></div>
      </div>
      <BuildingOverviewOnboarding building={building.id}/>
      {attention}
      <details className="building-reference-information"><summary>Building location, compliance and approved image</summary><div className="building-operational-row"><section aria-label="Building compliance"><h2>Compliance</h2>{canViewCompliance?<><Link to={withBuildingScope('/compliance',building.id)}>Review compliance issues and evidence</Link></>:<p>Compliance access is required to view this information.</p>}</section><BuildingAddressLocation key={building.id} building={building} setup={setup} canManage={canManage} complianceTruth={complianceTruth}/>
      <section aria-label="Approved building image">{canViewPhoto?<BuildingPhoto building={building.id} name={displayName}/>:<><h2>Building image</h2><p>Document access is required to view the approved image.</p></>}</section></div>
      </details>
      <details className="building-setup-gaps">
        <summary>Recorded setup · {readinessItems.length + informationGaps.length ? `${readinessItems.length + informationGaps.length} checks need attention` : 'checks complete'}</summary>
        <p>Setup checks describe missing building information.</p>
        {(readinessItems.length > 0 || informationGaps.length > 0) && <ul>
          {readinessItems.map(([area, status]) => <li key={area}><strong>{area === 'structure' ? 'Homes and spaces' : area === 'contacts' ? 'People' : area === 'regulatory' ? 'Regulatory profile' : 'Building features'}:</strong> {status}</li>)}
          {informationGaps.map(gap => <li key={gap}>{gap}</li>)}
        </ul>}
        {Number(setup.unaddressed_unit_count)>0&&<p>{setup.unaddressed_unit_count} premises have incomplete addresses; they remain live.</p>}
        <Link to={withBuildingScope(`/buildings/${building.id}#building-details`,building.id)}>Review recorded building information</Link>
      </details>
      <div className="building-profile-note">{buildingType(setup)}</div>
    </section>
  );
}

export function BuildingDetail({section: requestedSection = "overview"}: {section?: "overview" | "information" | "settings"}) {
  const routerNavigate = useNavigate(),location=useLocation();
  const section = requestedSection === 'information' && new URLSearchParams(location.search).get('section') === 'access' ? 'access' : requestedSection;
  const canManage = useCapability('property-assets:manage');
  const session = useStaffSession();
  const { id } = useParams();
  const attentionModel=useBuildingOperationalAttention(section==='overview' ? id??'' : '');
  const navigate=(to:string,options?:NavigateOptions)=>{const back=safeWorkflowReturn(new URLSearchParams(location.search).get('returnTo'));return routerNavigate(back?withWorkflowReturn(to,back):to,options)};
  const [building, setBuilding] = useState<Building>();
  const [setupState, setSetup] = useState<Setup>();
  const setup = setupState as Setup;
  const [areaData, setAreas] = useState<Area[]>();
  const [assetData, setAssets] = useState<Asset[]>();
  const areas=areaData??[],assets=assetData??[];
  const [modules,setModules]=useState<Record<string,{loading:boolean;error?:unknown}>>({});
  const controllers=useRef(new Map<string,AbortController>());
  const canViewCompliance=section==='overview' && (session?.capabilities.includes('compliance:view')??false);
  const [complianceTruth, setComplianceTruth] = useState<ComplianceTruthProjection>();
  const loadSequence = useRef(0);
  const activePage=useRef(true);
  const setupSubmitting=useRef(false),setupReceipt=useRef<{fingerprint:string;key:string}|undefined>(undefined);
  const [managementReceipt,setManagementReceipt]=useState<ManagementStatusModel>();
  const [setupUncertain,setSetupUncertain]=useState(false);
  const [setupErrors,setSetupErrors]=useState<BuildingFieldErrors>({});
  const [setupSaveError,setSetupSaveError]=useState('');
  const [error, setError] = useState<unknown>();
  const [saving, setSaving] = useState("");
  const [setupNotice, setSetupNotice] = useState("");
  const [editingBuildingDetails, setEditingBuildingDetails] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [editingComposition, setEditingComposition] = useState(false);
  const load = (only?:string,retainSetup=false) => {
    if(!activePage.current||section==='access')return Promise.resolve();
    const requestId=only?loadSequence.current:++loadSequence.current;
    if(!only){controllers.current.forEach(controller=>controller.abort());setError(undefined);}
    if(!only||only==='identity')setBuilding(undefined);if((!only||only==='setup')&&!retainSetup)setSetup(undefined);if(!only||only==='tree')setAreas(undefined);if(!only||only==='assets')setAssets(undefined);if(!only||only==='compliance')setComplianceTruth(undefined);
    const initial=Object.fromEntries(['identity','setup',...(section==='overview'?['tree','assets']:[]),...(canViewCompliance?['compliance']:[])].filter(name=>!only||name===only).map(name=>[name,{loading:true}]));setModules(previous=>only?{...previous,...initial}:initial);
    const scoped=(path:string)=>withBuildingScope(path,id);
    const read=async<T,>(name:string,path:string,apply:(value:T)=>void)=>{if((only&&only!==name)||(section!=='overview'&&name!=='identity'&&name!=='setup'))return;controllers.current.get(name)?.abort();const active=new AbortController();controllers.current.set(name,active);try{const value=await api<T>(path,{signal:active.signal});if(active.signal.aborted||requestId!==loadSequence.current)return;apply(value);setModules(previous=>({...previous,[name]:{loading:false}}));}catch(cause){if(!active.signal.aborted&&requestId===loadSequence.current){if(name==='setup'&&retainSetup)setSetup(undefined);setModules(previous=>({...previous,[name]:{loading:false,error:cause}}));}}};
    return Promise.all([
      read<Building[]>('identity',scoped('/api/property-assets/developments'),rows=>{if(!Array.isArray(rows))throw reviewedApplicationError('Building identity is unavailable.');const match=rows.find(row=>row.id===id);if(!match)throw reviewedApplicationError('Building identity is unavailable.');setBuilding(match);}),
      read<Setup>('setup',scoped(`/api/property-assets/developments/${id}/setup`),value=>{if(value.id!==id||!value.readiness||!['structure','contacts','regulatory','features'].every(key=>['Ready','Attention','Blocked'].includes(value.readiness[key as keyof Setup['readiness']]))||!Array.isArray(value.keyContacts)||!Array.isArray(value.features)||!Array.isArray(value.initialAssets)||!Array.isArray(value.onboardingDocuments))throw reviewedApplicationError('Building setup does not match this context.');setSetup(value);}),
      read<Area[]>('tree',scoped(`/api/property-assets/developments/${id}/tree`),rows=>{if(!Array.isArray(rows)||rows.some(row=>!row||typeof row.id!=='string'||typeof row.node_type!=='string'||('development_id' in row&&row.development_id!==id)))throw reviewedApplicationError('Homes and spaces do not match this building.');setAreas(rows);}),
      read<Asset[]>('assets',scoped(`/api/property-assets/assets?developmentId=${id}`),rows=>{if(!Array.isArray(rows)||rows.some(row=>!row||typeof row.id!=='string'||('development_id' in row&&row.development_id!==id)))throw reviewedApplicationError('Assets do not match this building.');setAssets(rows);}),
      ...(canViewCompliance?[read<{compliance_truth?:ComplianceTruthProjection}>('compliance',scoped(`/api/compliance/summary?developmentId=${id}`),value=>{const truth=value.compliance_truth;if(!truth||truth.scope.kind!=='building'||truth.scope.development_id!==id)throw reviewedApplicationError('Compliance indication does not match this building.');setComplianceTruth(truth);})]:[]),
    ]);
  };
  useEffect(()=>{activePage.current=true;if(id)void load();return()=>{activePage.current=false;loadSequence.current+=1;controllers.current.forEach(controller=>controller.abort());};},[id,canViewCompliance,section]);
  async function saveComposition(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!id || saving === "composition") return;
    const data = new FormData(event.currentTarget);
    const commercialUnitStatus = String(data.get("commercialUnitStatus") ?? setup.commercial_unit_status);
    setSaving("composition");
    setError(undefined);
    try {
      await api(`/api/property-assets/developments/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: setup.name,
          line1: setup.line_1,
          line2: setup.line_2 ?? undefined,
          townCity: setup.town_city,
          county: setup.county ?? undefined,
          postcode: setup.postcode,
          countryCode: setup.country_code,
          managementStatus: setup.management_status,
          expectedResidentialUnits: Number(
            data.get("expectedResidentialUnits"),
          ),
          commercialUnitStatus,
          expectedCommercialUnits:
            commercialUnitStatus === "Yes"
              ? Number(data.get("expectedCommercialUnits"))
              : undefined,
          expectedCommunalFacilities: setup.expected_communal_facilities,
          managementStartDate: setup.management_start_date?.slice(0,10) ?? undefined,
          managementEndDate: setup.management_end_date?.slice(0,10) ?? undefined,
          onboardingNarrative: setup.onboarding_narrative ?? undefined,
          buildingUse: setup.building_use,
          heightMetres: setup.height_metres == null ? undefined : Number(setup.height_metres),
          storeys: setup.storeys ?? undefined,
          highRiseStatus: setup.high_rise_status,
          hrbStatus: setup.hrb_status,
          features: setup.features.map(feature=>({...feature,notes:feature.notes??undefined})),
          initialAssets: setup.initialAssets.map((item) => ({
            sourceHash: item.sourceHash,
            code: item.code,
            label: item.label,
            category: item.category,
            type: item.type,
            criticality: item.criticality,
            presence: item.presence,
            quantityStatus: item.quantity_status,
            quantity: item.quantity ?? undefined,
            location: item.location ?? undefined,
            notes: item.notes ?? undefined,
          })),
          sourceNote: "Declared building composition corrected",
        }),
      });
      if(!activePage.current)return;
      await load();
      if(!activePage.current)return;
      setEditingComposition(false);
      setSetupNotice(`Premises counts saved at ${formatDateTime(new Date())}.`);
    } catch (next) {
      setError(next);
    } finally {
      setSaving("");
    }
  }
  const orderedAreas=[...areas].sort((a,b)=>a.display_name.localeCompare(b.display_name,'en-GB',{numeric:true,sensitivity:'base'}));
  const residential = orderedAreas.filter((item) =>
    item.premises_type?.toLowerCase().startsWith("residential"),
  );
  const commercial = orderedAreas.filter((item) =>
    item.premises_type?.toLowerCase().startsWith("commercial"),
  );
  const communal = areas.filter(
    (item) =>
      item.premises_type === "Ancillary area" ||
      item.premises_type === "Management supply location" ||
      item.node_type === "Common area" ||
      item.node_type === "Land",
  );
  async function saveSetup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!id || !canManage || setupSubmitting.current || setupUncertain) return;
    setupSubmitting.current=true;
    const form=event.currentTarget;
    const data = new FormData(form);
    const features = featureOptions.map(([code, label, category]) => ({
      code,
      label,
      category,
      status: String(data.get(`feature-${code}`) ?? setup.features.find(item=>item.code===code)?.status ?? "Unknown"),
    }));
    const commercialUnitStatus = String(data.get("commercialUnitStatus") ?? setup.commercial_unit_status);
    const featureCodes = new Set<string>(featureOptions.map(([code]) => code));
    const featureAssets = featureOptions.map(([code, label, category]) => {
      const savedAsset = setup.initialAssets.find(item=>item.code===code);
      const status = String(data.get(`feature-${code}`) ?? setup.features.find(item=>item.code===code)?.status ?? "Unknown");
      const definition = featureAssetDefinitions[code]!;
      const quantityValue = String(data.get(`feature-quantity-${code}`) ?? savedAsset?.quantity ?? "");
      return {
        code,
        label,
        category,
        type: definition.type,
        sourceHash: savedAsset?.sourceHash,
        criticality: definition.criticality,
        presence:
          status === "Present"
            ? "Present"
            : status === "Not present"
              ? "Not present"
              : "To confirm",
        quantityStatus:
          status === "Present" && definition.quantity
            ? quantityValue
              ? "Known"
              : "To confirm"
            : "Not applicable",
        quantity: status==='Present'&&definition.quantity&&quantityValue ? Number(quantityValue) : undefined,
        location:
          String(data.get(`feature-location-${code}`) ?? savedAsset?.location ?? "") || undefined,
        notes: String(data.get(`feature-notes-${code}`) ?? savedAsset?.notes ?? "") || undefined,
      };
    });
    setSaving("setup");
    setError(undefined);
    setSetupNotice("");
    setSetupSaveError('');
    const payload={
          detailsOnly:section === "settings",
          name: data.get("name") ?? setup.name,
          code: data.get("code") ?? setup.code,
          line1: data.get("line1") ?? setup.line_1,
          line2: data.has("line2") ? data.get("line2") || undefined : setup.line_2 ?? undefined,
          townCity: data.get("townCity") ?? setup.town_city,
          county: data.has("county") ? data.get("county") || undefined : setup.county ?? undefined,
          postcode: data.get("postcode") ?? setup.postcode,
          countryCode: setup.country_code??"GB",
          communalAreas:data.has("communalAreasDeclared")?data.getAll("communalAreas").map(String):setup.communal_areas??undefined,
          communalAreaOther:data.has("communalAreasDeclared")?String(data.get("communalAreaOther")??""):setup.communal_area_other??undefined,
          managementStatus: data.get("managementStatus") ?? setup.management_status,
          expectedResidentialUnits: Number(
            data.get("expectedResidentialUnits") ?? setup.expected_residential_units,
          ),
          commercialUnitStatus,
          expectedCommercialUnits:
            commercialUnitStatus === "Yes"
              ? Number(data.get("expectedCommercialUnits") ?? setup.expected_commercial_units)
              : undefined,
          expectedCommunalFacilities: Number(
            data.get("expectedCommunalFacilities") ?? setup.expected_communal_facilities,
          ),
          managementStartDate: data.has("managementStartDate") ? data.get("managementStartDate") || undefined : setup.management_start_date?.slice(0,10),
          managementEndDate: data.has("managementEndDate") ? data.get("managementEndDate") || undefined : setup.management_end_date?.slice(0,10),
          onboardingNarrative: data.has("onboardingNarrative") ? data.get("onboardingNarrative") || undefined : setup.onboarding_narrative ?? undefined,
          buildingUse: data.get("buildingUse") ?? setup.building_use,
          heightMetres:
            data.has("heightMetres") ? (data.get("heightMetres") === "" ? undefined : Number(data.get("heightMetres"))) : setup.height_metres == null ? undefined : Number(setup.height_metres),
          storeys:
            data.has("storeys") ? (data.get("storeys") === "" ? undefined : Number(data.get("storeys"))) : setup.storeys ?? undefined,
          highRiseStatus: data.get("highRiseStatus") ?? setup.high_rise_status,
          hrbStatus: setup.hrb_status,
          features: section === "settings" ? setup.features.map(item=>({...item,notes:item.notes??undefined})) : features,
          initialAssets: section === "settings" ? setup.initialAssets.map(item=>({code:item.code,label:item.label,category:item.category,sourceHash:item.sourceHash,type:item.type,criticality:item.criticality,presence:item.presence,quantityStatus:item.quantity_status,quantity:item.quantity??undefined,location:item.location??undefined,notes:item.notes??undefined})) : [
            ...setup.initialAssets
              .filter((item) => !featureCodes.has(item.code))
              .map((item) => ({
                code: item.code,
                label: item.label,
                category: item.category,
                type: item.type,
                criticality: item.criticality,
                presence: item.presence,
                quantityStatus: item.quantity_status,
                quantity: item.quantity ?? undefined,
                location: item.location ?? undefined,
                notes: item.notes ?? undefined,
              })),
            ...featureAssets,
          ],
          sourceNote: section === "settings" ? "Building details reviewed and saved" : "Manual building mobilisation reviewed and saved",
        };
    const fields=buildingSetupErrors(payload);
    setSetupErrors(fields);showFormErrors(form,fields);
    if(Object.keys(fields).length){setEditingBuildingDetails(true);requestAnimationFrame(()=>showFormErrors(form,fields));setupSubmitting.current=false;setSaving('');return;}
    try {
      const fingerprint=JSON.stringify(payload);if(setupReceipt.current?.fingerprint!==fingerprint)setupReceipt.current={fingerprint,key:crypto.randomUUID()};
      const receipt=await saveRequest<{id:string;savedAt:string;operational:ManagementStatusModel}>(withBuildingScope(`/api/property-assets/developments/${id}`,id),{method:'PATCH',body:JSON.stringify({...payload,idempotencyKey:setupReceipt.current.key})});
      if(!activePage.current)return;
      if(!receipt || receipt.id!==id){setSetupUncertain(true);throw reviewedApplicationError("The building save receipt could not be verified.");}
      setManagementReceipt(receipt.operational);
      setSetup(previous=>previous?{...previous,name:String(payload.name),management_start_date:payload.managementStartDate?String(payload.managementStartDate):'',management_end_date:payload.managementEndDate?String(payload.managementEndDate):'',line_1:String(payload.line1),line_2:payload.line2?String(payload.line2):'',town_city:String(payload.townCity),postcode:String(payload.postcode),expected_residential_units:payload.expectedResidentialUnits,commercial_unit_status:payload.commercialUnitStatus as Setup['commercial_unit_status'],expected_commercial_units:payload.expectedCommercialUnits??0}:previous);
      setEditingBuildingDetails(false);
      // Refresh supporting facts separately. A committed save is already confirmed.
      if(section!=="settings")void load();else void load('setup',true);
      setSetupNotice(
        section === "settings" ? `Building details saved at ${formatDateTime(new Date())}.` : `Mobilisation setup saved at ${formatDateTime(new Date())}. Supporting building panels are refreshing.`,
      );
    } catch (next) {
      if(!activePage.current)return;
      const fields=isApiError(next)?next.fieldErrors:{};
      if(Object.keys(fields).length){setSetupErrors(fields);showFormErrors(form,fields);setEditingBuildingDetails(true);}
      else setSetupSaveError(isApiError(next)&&next.code==='validation_failed'?'We couldn’t save the building. Review the entered details and try again.':'We couldn’t confirm the save. Your entries are preserved. Retry to save these same details.');
    } finally {
      setupSubmitting.current=false;
      if(activePage.current)setSaving("");
    }
  }
  async function createUnits(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!id) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const start = Number(data.get("start"));
    const end = Number(data.get("end"));
    const prefix = String(data.get("prefix") || "Flat").trim();
    if (
      !Number.isInteger(start) ||
      !Number.isInteger(end) ||
      end < start ||
      end - start > 199
    ) {
      setError(reviewedApplicationError("Enter a valid range of up to 200 units"));
      return;
    }
    const units = Array.from({ length: end - start + 1 }, (_, index) => {
      const value = start + index;
      return {
        premisesCode: `${prefix}-${value}`.replace(/\s+/g, "-").toUpperCase(),
        displayName: `${prefix} ${value}`,
        premisesType: data.get("premisesType"),
      };
    });
    setSaving("units");
    setError(undefined);
    try {
      await api(`/api/property-assets/developments/${id}/premises`, {
        method: "POST",
        body: JSON.stringify({
          units,
          sourceNote: "Units created from reviewed building setup",
        }),
      });
      form.reset();
      if(!activePage.current)return;
      await load();
      if(!activePage.current)return;
    } catch (next) {
      setError(next);
    } finally {
      setSaving("");
    }
  }
  const names:Record<string,string>={identity:'Building identity',setup:'Building setup',tree:'Homes and spaces',assets:'Assets',compliance:'Compliance indication'};
  const uncertainSetupState=setupUncertain&&<State tone="warning" kind="unavailable" heading="Check the saved building details" headingLevel={2} message="The save could not be confirmed. Review the recorded building details before starting another edit." secondaryAction={{label:'Review saved details',onClick:()=>navigate(withBuildingScope(`/buildings/${id}/information`,id))}}/>;
  const moduleStates=<>{Object.entries(modules).filter(([,state])=>state.loading||state.error).map(([name,state])=><State tone="warning" key={name} kind={state.loading?'loading':'unavailable'} heading={`${names[name]} ${state.loading?'loading':'unavailable'}`} headingLevel={2} message={state.loading?'This section is still loading. Other available building information remains usable.':'This section could not be loaded. Its counts and dependent editing are unavailable.'} {...(state.error?{retry:{onRetry:()=>void load(name)}}:{})}/>)}</>;
  const operationalAttention=<>{moduleStates}{uncertainSetupState}{section==='overview'&&id&&<BuildingOperationalAttention model={attentionModel} truth={complianceTruth}/>}</>;
  if(section==='access')return <><header className="page-heading"><h1>Keys and access</h1><Link to={withBuildingScope(`/buildings/${id}/setup`,id)}>Building Setup</Link></header><BuildingKeysAccess key={id} building={id!}/><SetupAccess key={`evidence-${id}`} building={id!} onSaved={()=>{}}/></>;
  if (!building || !setup) return <div className="operational-dashboard building-dashboard-partial"><section className="panel building-section"><Link to="/buildings">Buildings</Link><h1>{(building?.name??setup?.name??'Building workspace').replace(' - Controlled Demo','')}</h1><p>Available sections are shown independently. Missing information is not confirmation of readiness.</p></section>{setupNotice&&<p role="status" className="success">{setupNotice}</p>}{operationalAttention}{Boolean(error)&&<ErrorState error={error}/>}</div>;

  const setupFeedback=<>{setupNotice&&setup.mobilisationProgrammeId&&<Link className="wide-field" to={withBuildingScope(`/buildings/${id}/setup`,id)}>Continue Building Setup</Link>}{Object.keys(setupErrors).length>0&&<div role="alert" className="wide-field form-error-summary">We couldn’t save the building. Please correct {Object.keys(setupErrors).length===1?'the highlighted field':`the ${Object.keys(setupErrors).length} highlighted fields`}.</div>}{setupSaveError&&<p role="alert" className="wide-field">{setupSaveError}</p>}{setup.configuration?.source==='front-door-executed-agreement'&&<p className="wide-field form-help">These details came from the signed agreement. Check the address and complete any missing information before continuing.</p>}</>;
  const settingsForm = <section className="panel building-section"><div className="section-heading"><div><h2>{section === "settings" ? "Recorded details" : "Building settings"}</h2><p>{section === "settings" ? "Update this building’s recorded details." : "Update the details that rarely change. Contract dates may also be populated from an approved management contract."}</p></div></div><form className={section === "settings" ? "setup-grid building-settings-grid" : "setup-grid"} onSubmit={saveSetup} noValidate>
          {setupFeedback}
          <fieldset className="building-settings-group"><legend>Identity</legend><div className="building-settings-fields"><label>Building name<input name="name" required defaultValue={setup.name}/></label>
          <label>Short building reference<input name="code" required maxLength={30} pattern="[A-Za-z0-9-]+" defaultValue={setup.code}/><small>A unique reference such as BEULAH or MYFM123.</small></label>
          <label>Contract stage<select name="managementStatus" defaultValue={setup.management_status}>{["Enquiry","Mobilising","In contract","Notice given","Lost block","Archived"].map(value=><option key={value}>{value}</option>)}</select></label>
          </div></fieldset><fieldset className="building-settings-group"><legend>Address</legend><div className="building-settings-fields"><label>Address line 1<input name="line1" required defaultValue={setup.line_1}/></label>
          <label>Address line 2 (optional)<input name="line2" defaultValue={setup.line_2}/></label>
          <label>Town or city<input name="townCity" required defaultValue={setup.town_city}/></label>
          <label>County (optional)<input name="county" defaultValue={setup.county}/></label>
          <label>Postcode<input name="postcode" required defaultValue={setup.postcode}/></label>
          </div></fieldset><fieldset className="building-settings-group"><legend>Building profile and management dates</legend><div className="building-settings-fields"><label>Number of residential premises<input name="expectedResidentialUnits" type="number" min="0" required defaultValue={setup.expected_residential_units}/></label>
          <CommercialDeclarationFields setup={setup}/>
          <p><Link to={withBuildingScope(`/buildings/${id}/mobilisation/survey`,id)}>Review communal facilities and safety systems</Link></p>
          <label>Client since<DateOnlyInput name="managementStartDate" defaultValue={setup.management_start_date?.slice(0,10)}/><small>Management start date</small></label>
          <label>Management ends (optional)<DateOnlyInput name="managementEndDate" defaultValue={setup.management_end_date?.slice(0,10)}/></label>
          <label>Building use<select name="buildingUse" defaultValue={setup.building_use}>{["Residential","Mixed use","Commercial","Other","Unknown"].map(value=><option key={value}>{value}</option>)}</select></label>
          <label>Storeys<input name="storeys" type="number" min="0" defaultValue={setup.storeys}/></label>
          <label>Height in metres (optional)<input name="heightMetres" type="number" min="0" max="1000" step="any" defaultValue={setup.height_metres}/><small>Leave blank if unknown.</small></label>
          <label>High-rise building (18 metres or more)?<select name="highRiseStatus" defaultValue={setup.high_rise_status}><option>To confirm</option><option>Yes</option><option>No</option></select></label>
          </div></fieldset><fieldset className="building-settings-group"><legend>Background</legend><div className="building-settings-fields"><label className="wide-field">Management background (optional)<textarea name="onboardingNarrative" rows={3} defaultValue={setup.onboarding_narrative??''}/></label>
          </div></fieldset><div className="building-settings-save"><button disabled={saving==="setup"||setupUncertain}>{saving==="setup"?"Saving…":"Save building details"}</button><Link className="button-link secondary" to={withBuildingScope(`/buildings/${id}/information`,id)}>Cancel</Link>
        </div></form></section>;
  const recordedDetails=<section id="building-details" tabIndex={-1} className="panel building-section" aria-label="Building details">
    <div className="section-heading"><h2>Building details</h2>{canManage&&<Link to={withBuildingScope(`/buildings/${id}/settings`,id)}>Edit</Link>}</div>
    <dl className="detail-grid">
      {/* Audit F-097: the internal code is an implementation detail (set under "Advanced internal reference" when a building is created) and is not shown to operators. */}
      {/demo|test/i.test(setup.code)&&<div><dt>Internal reference</dt><dd><p>Review this building's internal reference with an administrator; it has not been changed.</p></dd></div>}
      <div><dt>Recorded lifecycle status</dt><dd>{setup.management_status}</dd></div>
      <div><dt>Recorded management start</dt><dd>{formatDateOnly(setup.management_start_date?.slice(0,10),'Not recorded')}</dd></div>
      <div><dt>Recorded management end</dt><dd>{formatDateOnly(setup.management_end_date?.slice(0,10),'Not recorded')}</dd></div>
      <div><dt>Communal facilities</dt><dd>{setup.communal_areas?.join(", ")||"Not yet reviewed"}{setup.communal_area_other?` — ${setup.communal_area_other}`:""}</dd></div>
      <div><dt>Building use</dt><dd>{setup.building_use||'Not recorded'}</dd></div>
      <div><dt>Storeys</dt><dd>{setup.storeys??'Not recorded'}</dd></div>
      <div><dt>Height (metres)</dt><dd>{setup.height_metres??'Not recorded'}</dd></div>
      <div><dt>High-rise status</dt><dd>{setup.high_rise_status||'To confirm'}</dd></div>
      <div><dt>Higher-risk building status</dt><dd>{setup.hrb_status||'Requires assessment'}</dd></div>
    </dl><p>{section==='information'?'Declared property totals appear above; the address and map location follow below.':'The address, declared unit totals and operational management dates are shown in the summary above. Recorded dates remain available here if operational status cannot be loaded.'} Lifecycle status records the building's management stage; operational status also accounts for its agreed dates. Building declarations do not establish compliance.</p>
  </section>;
  if(section==='information')return <div className="building-information-workspace">
    <header className="page-heading"><div><h1>Building information</h1><p>{building.name.replace(' - Controlled Demo','')}</p></div>{canManage&&<Link className="button-link" to={withBuildingScope(`/buildings/${id}/settings`,id)}>Edit information</Link>}</header>
    <dl className="building-reference-facts"><div><dt>Recorded stage</dt><dd>{setup.management_status??setup.status}</dd></div><div><dt>Management start</dt><dd>{formatDateOnly(setup.management_start_date,'Not recorded')}</dd></div><div><dt>Higher-risk building</dt><dd>{setup.hrb_status||'Requires assessment'}</dd></div><div><dt>Residential / commercial</dt><dd><Link to={withBuildingScope(`/buildings/${id}/mobilisation/premises`,id)}>{setup.expected_residential_units??'Unknown'} flats · {commercialDeclarationLabel(setup.commercial_unit_status,setup.expected_commercial_units)}</Link></dd></div><div><dt>Height / storeys</dt><dd>{setup.height_metres==null?'Height not recorded':`${setup.height_metres} m`} · {setup.storeys??'Unknown'} storeys</dd></div><div><dt>Building use</dt><dd>{setup.building_use||'Not recorded'}</dd></div></dl>
    <p className="building-information-address">{[setup.line_1,setup.line_2,setup.town_city,setup.county,setup.postcode].filter(Boolean).join(', ')||'Address not recorded'}</p>
    {session?.capabilities.includes('documents:view')&&<KeyDocuments developmentId={building.id}/>}
    <LazyDetails summary="Full facts, location and map" openForHashes={['#building-details']}>{recordedDetails}<BuildingPropertyCounts setup={setup}/><BuildingAddressLocation key={building.id} building={building} setup={setup} canManage={canManage} focused/></LazyDetails>
    {session?.permittedDestinations.some(item=>item.id==='audit')&&<LazyDetails summary="Building history"><TimelinePanel developmentId={building.id}/></LazyDetails>}
  </div>;
  if(section==="settings")return <>
    <div className="page-heading"><div><Link to={withBuildingScope(`/buildings/${id}`,id)}>Building overview</Link><h1>Building settings</h1><p>{setup.name.replace(' - Controlled Demo','')}</p></div><Link to={withBuildingScope(`/buildings/${id}/information`,id)}>Building details</Link></div>
    {moduleStates}{error&&<ErrorState error={error}/>}{setupNotice&&<p role="status" className="success">{setupNotice}</p>}{uncertainSetupState}
    <BuildingManagementStatus building={id!} editable receipt={managementReceipt}/>{canManage&&settingsForm}
  </>;
  const hasExpectedPremises=setup.recorded_residential_units>=setup.expected_residential_units&&(setup.commercial_unit_status==="No"||setup.commercial_unit_status==="Yes"&&setup.expected_commercial_units!==null&&setup.recorded_commercial_units>=(setup.expected_commercial_units??0));
  const structureComplete =
    residential.length >= setup.expected_residential_units &&
    (setup.commercial_unit_status !== "Yes" ||
      commercial.length >= (setup.expected_commercial_units ?? 0)) &&
    communal.length >= setup.expected_communal_facilities;

  const canViewDocuments = session?.permittedDestinations.some(item => item.id === 'documents') ?? false;
  const canViewWork = session?.capabilities.includes('work:view') ?? false;
  const canViewPeople = session?.permittedDestinations.some(item => item.id === 'people') ?? false;
  const canViewAudit = session?.permittedDestinations.some(item => item.id === 'audit') ?? false;
  if (!canManage||!areaData||!assetData) return <>
    <BuildingSummary
      building={building}
      setup={setup}
      communal={areaData?communal.length:undefined}
      assets={assetData?assets.length:undefined}
      complianceTruth={canViewCompliance ? complianceTruth : undefined}
      onEdit={() => navigate(withBuildingScope(`/buildings/${building.id}/settings`,building.id))}
      canManage={canManage}
      attention={operationalAttention}
    />
    {error && <ErrorState error={error} />}
    <LazyDetails summary="Full building information" openForHashes={['#building-details']}>{recordedDetails}</LazyDetails>
    <LazyDetails summary="Building setup checks"><BuildingSetupSummary id={id!}/></LazyDetails>
  </>;
  return (
      <>
        <BuildingSummary
          building={building}
          setup={setup}
          communal={areaData?communal.length:undefined}
          assets={assetData?assets.length:undefined}
          complianceTruth={canViewCompliance ? complianceTruth : undefined}
          onEdit={() => navigate(withBuildingScope(`/buildings/${building.id}/settings`,building.id),{state:{buildingArea:true}})}
          canManage={canManage}
      attention={operationalAttention}
        />
        {error && <ErrorState error={error} />}

        {canViewDocuments && <PendingDocumentsCard developmentId={building.id} compact />}
        <LazyDetails summary="Building setup checks"><BuildingSetupSummary id={id!}/></LazyDetails>

        <div id="properties">
          {canManage&&!hasExpectedPremises&&<p><Link to={withBuildingScope(`/buildings/${building.id}/mobilisation/premises`,building.id)}>Open premises register</Link></p>}
          {residential.length + commercial.length > 0 && (
            <UnitSelector
              buildingId={building.id}
              residential={residential}
              commercial={commercial}
              onSelect={(path) => navigate(withBuildingScope(path, id))}
            />
          )}
        </div>
        <LazyDetails className="mobilisation-related" summary="Related building records and history" openForHashes={['#activity']}>
        {canViewPeople && <section className="panel building-section">
          <div className="section-heading">
            <div>
              <h2>Key contacts</h2>
              <p>
                People and organisations with current roles at this building.
              </p>
            </div>
            <Link to={`/contacts?developmentId=${building.id}`}>
              Manage in Contacts <Icon name="arrow-right"/>
            </Link>
          </div>
          {!setup.keyContacts.length ? (
            <Empty>No active building contacts are assigned yet.</Empty>
          ) : (
            <div className="simple-list">
              {setup.keyContacts.map((contact) => (
                <div key={contact.id}>
                  <strong>{contact.contact_name}</strong>
                  <span>
                    {contact.role_name}
                    {contact.is_primary_contact ? " · Primary" : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>}
        {communal.length > 0 && (
          <PremisesList buildingId={building.id} title="Communal facilities" items={communal} empty="" />
        )}
        {canViewDocuments && <KeyDocuments developmentId={building.id} />}
        {assets.length > 0 && (
          <section className="panel building-section">
            <div className="section-heading">
              <div>
                <h2>Building assets</h2>
                <p>
                  Equipment and building components recorded at this building.
                </p>
              </div>
              <Link to={`/assets?developmentId=${building.id}`}>
                Open asset register <Icon name="arrow-right"/>
              </Link>
            </div>
            <div className="asset-list">
              {assets.slice(0, 5).map((asset) => (
                <Link key={asset.id} to={`/assets/${asset.id}`}>
                  <div>
                    <span className="badge">{asset.asset_category}</span>
                    <strong>
                      {asset.asset_code} · {asset.display_name}
                    </strong>
                    <small>
                      {asset.asset_type} · {asset.property_name}
                    </small>
                  </div>
                  <span>
                    {asset.status} · {asset.criticality}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
        {canViewDocuments && <div aria-label="Files & Documents">
          <RelatedDocuments developmentId={building.id} />
        </div>}
        {canViewCompliance && <RelatedCompliance developmentId={building.id} truth={complianceTruth} />}
        {canViewWork && <RelatedWork developmentId={building.id} />}
        {canViewAudit && <div id="activity">
          <TimelinePanel developmentId={building.id} />
        </div>}
        </LazyDetails>
        <LazyDetails summary="Full building information" openForHashes={['#building-details']}>{recordedDetails}</LazyDetails>
      </>
  );
}
